import type { RootState } from "@app/store";
import { createSelector } from "@reduxjs/toolkit";
import { getVisibleProperties } from "@shared/utils/propertyFiltering";
import type {
  Property,
  PropertyWithOrder,
} from "@survey-types/survey.property.types";

// Base selectors - these access the state directly
const selectSurveyState = (state: RootState) => state.survey;
export const selectCurrentSurvey = (state: RootState) =>
  state.survey.currentSurvey;

export const selectCurrentSurveyId = createSelector(
  [selectCurrentSurvey],
  (currentSurvey) => currentSurvey?.id,
);

export const selectCurrentPropertyId = createSelector(
  [selectSurveyState],
  (surveyState) => surveyState.currentPropertyId,
);

export const selectProperties = createSelector(
  [selectCurrentSurvey],
  (currentSurvey) => currentSurvey?.properties || [],
);

export const selectPropertiesWithOrder = createSelector(
  [selectProperties],
  (properties): PropertyWithOrder[] =>
    properties.map((prop: Property) => ({
      ...prop,
      isVisible: !prop.isHidden,
    })),
);

export const selectMediaFiles = createSelector(
  [selectSurveyState],
  (surveyState) => surveyState.mediaFiles,
);

export const selectIsLoading = createSelector(
  [selectSurveyState],
  (surveyState) => surveyState.isLoading,
);

export const selectError = createSelector(
  [selectSurveyState],
  (surveyState) => surveyState.error,
);

export const selectEditingContactId = createSelector(
  [selectSurveyState],
  (surveyState) => surveyState.editingContactId,
);

// Specific property selector
export const selectCurrentProperty = createSelector(
  [selectProperties, selectCurrentPropertyId],
  (properties, currentPropertyId) =>
    properties.find((property) => property.id === currentPropertyId),
);

// Paper size selector for PDF exports
export const selectPaperSize = createSelector(
  [selectCurrentSurvey],
  (currentSurvey) => currentSurvey?.pdfPaperSize,
);

// Survey configuration selectors for preview components
export const selectSurveyConfig = createSelector(
  [selectCurrentSurvey],
  (currentSurvey) => ({
    isIncludePhotos: currentSurvey?.isIncludePhotos,
    isIncludeSitePlans: currentSurvey?.isIncludeSitePlans,
    isIncludeFlyers: currentSurvey?.isIncludeFlyers,
    pdfPaperSize: currentSurvey?.pdfPaperSize,
    showCurrentLocationPD: currentSurvey?.showCurrentLocationPD,
    sitePlanLabel: currentSurvey?.sitePlanLabel ?? "Site Plans",
    flyerLabel: currentSurvey?.flyerLabel ?? "Flyers",
  }),
);

// Property media selector - takes propertyId as parameter
export const selectPropertyMedia = (propertyId: string) =>
  createSelector(
    [selectMediaFiles],
    (mediaFiles) => mediaFiles?.[propertyId] || null,
  );

// Visible properties selector (replaces useVisibleProperties logic)
// Uses showCurrentLocationPD flag for property details section
export const selectVisibleProperties = createSelector(
  [selectCurrentSurvey, selectProperties],
  (survey, properties) =>
    getVisibleProperties(properties, survey, undefined, true),
);

// Property field rules selector
export const selectPropertyFieldRules = createSelector(
  [selectCurrentSurvey],
  (currentSurvey) =>
    currentSurvey?.surveyPropertyFieldRule?.propertyFieldRuleList || [],
);

// Survey contacts selector
export const selectSurveyContacts = createSelector(
  [selectCurrentSurvey],
  (currentSurvey) => currentSurvey?.contacts || [],
);

// Survey sections selector
export const selectSurveySections = createSelector(
  [selectCurrentSurvey],
  (currentSurvey) => currentSurvey?.sections || [],
);

// Custom fields selector
export const selectCustomFields = createSelector(
  [selectCurrentSurvey],
  (currentSurvey) => currentSurvey?.customFields || [],
);

// Properties drawer open state selector
export const selectIsPropertiesDrawerOpen = createSelector(
  [selectSurveyState],
  (surveyState) => surveyState.isPropertiesDrawerOpen,
);

// Market map keys selector
export const selectMarketMapKeys = createSelector(
  [selectSurveyState],
  (surveyState) => surveyState.marketMapKeys,
);

// POI inclusion flag selector
// Memoized to prevent unnecessary re-renders when survey object changes
export const selectIsIncludePOI = createSelector(
  [selectCurrentSurvey],
  (currentSurvey) => currentSurvey?.isIncludePOI ?? false,
);
